import { makeStyles } from "tss-react/mui";
import { Chip, Divider, Grid, Stack, Table, TableBody, TableCell, TableRow } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMission } from "../../../../../providers/MissionProvider";
import { useDevice } from "../../../../../providers/DeviceProvider";
import { MissionEnumsLists, TaskEnums, TaskEnumsLists } from "../../../../../common/constants";
import { formatText } from "../../../../../common/helpers";
import { FormatLocation } from "../../../../../common/maphelpers";
import { Defaults, useEntityField } from "@emberly/zenith-client";
import DateAndTimePicker from "../../../inputs/DateAndTimePicker";
import DriverDetails from "../../../../pages/mission/cards/tasks/common/DriverDetails";
import StateCard from "../../../../pages/mission/cards/tasks/common/StateCard";
import RouteDetailsCard from "../../../map/RouteDetailsCard";
import { useMemo } from "react";
import config from "../../../../../config";
import { FullTimeView, TimeView } from "../../../core/Time";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      width: "100%"
    },
    table: {
      width: "100%",
      "& td": {
        border: "none",
        padding: theme.spacing(1),
        paddingLeft: 0,
        paddingBottom: theme.spacing(0.5),
        paddingTop: theme.spacing(0.5),
        verticalAlign: "top"
      },
      "& tr > :first-of-type": {
        fontWeight: 500
      }
    },
    image: {
      borderRadius: theme.spacing(1),
      background: theme.palette.divider,
      height: "64px",
      width: "64px",
      objectFit: "cover"
    },
    divider: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    }
  })
);

export default function QuickViewDetails(props) {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { task, route } = props;
  const { mission } = useMission();
  const { slim } = useDevice();


  const path = `salvageTasks.${task.taskIndex}`;
  const estimatedArrival = useEntityField(`${path}.execution.estimatedArrival`, Defaults.String);


  const actors = mission.actors;
  const targetType = mission.target.type;
  const targetDetails = targetType === 1 ? mission.target.carDetails : (targetType === 2 ? mission.target.boatDetails : null);
  const formattedTarget = formatText(" ", targetDetails?.make, targetDetails?.model) || t(`mission:enums:targetType:${MissionEnumsLists.TargetType[targetType]}`);

  const waypoints = task.route?.waypoints;
  const location = waypoints[0];
  const transport = waypoints.length > 1 ? waypoints.findLast(t => !!t.address) : null;

  const images = useMemo(() => {
    const files = Object.values(mission.files.uploads).filter(f => !!f && !!f.complete && !!f.url);
    return files.filter(t => t.type === 1).map(t => ({
      ...t,
      preview: `${t.url.replace(`https://storage.googleapis.com/assist-private-${config.isProd ? "prod" : "dev"}`, "https://ik.imagekit.io/assist/")}?tr=h-64`
    }));
  }, [mission]);


  const { plannedType, plannedStart, plannedEnd } = task;


  return (
    <Stack className={classes.root} spacing={1}>

      {
        slim ? (
          <>
            <RouteDetailsCard in={!!route} route={route} path={path} />

            <DateAndTimePicker
              label={t("mission:cards:tasks:salvageTask:eta")}
              value={estimatedArrival.value}
              onChange={estimatedArrival.onChange}
              includeTime
              fromNow
            />

            <DriverDetails
              path={path}
              taskId={task.taskId}
            />

            <StateCard path={path} />
          </>
        ) : null
      }

      <Table className={classes.table}>
        <TableBody>

          <TableRow>
            <TableCell>
              {t("taskDetailsCard:planned")}
            </TableCell>
            <TableCell>
              {
                plannedType === TaskEnums.PlanningType.Interval ? (
                  <>
                    {t("mission:cards:tasks:planning:from")} <TimeView time={plannedStart} /><br />
                    {t("mission:cards:tasks:planning:to")} <TimeView time={plannedEnd} />
                  </>
                ) : plannedType === TaskEnums.PlanningType.Time ? (
                  <FullTimeView time={plannedEnd} />
                ) : (
                  t("task:enums:plannedType:None")
                )
              }
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>{t("taskQuickView:priority")}</TableCell>
            <TableCell>{t(`task:enums:priority:${TaskEnumsLists.Priority[task.priority || 0]}`)}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell>{t("taskQuickView:missionDetails")}</TableCell>
            <TableCell>{mission.details.description || "-"}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell>{t("taskQuickView:cause")}</TableCell>
            <TableCell>{mission.details.cause?.name || "-"}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell>{t("common:tables:tags")}</TableCell>
            <TableCell>
              <Grid item container xs spacing={0.5} className={classes.chipContainer}>
                {mission?.details?.tags?.map((tag, i) => (
                  <Grid item key={i}>
                    <Chip label={tag.name} size="small" />
                  </Grid>
                ))}
              </Grid>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>{t("common:tables:object")}</TableCell>
            <TableCell>{formattedTarget || "-"}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell>{t("mission:cards:target:registration")}</TableCell>
            <TableCell>{targetDetails?.registration || "-"}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell>{t("mission:cards:actors:actors:requester")}</TableCell>
            <TableCell>{(actors.ownerIsNotRequester ? actors?.requester?.name : actors?.owner?.name) || "-"}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell>{t("common:phoneNumber")}</TableCell>
            <TableCell>{(actors.ownerIsNotRequester ? actors?.requester?.phoneNumber : actors?.owner?.phoneNumber) || "-"}</TableCell>
          </TableRow>

          <TableRow>
            <TableCell colSpan={2}><Divider className={classes.divider} /></TableCell>
          </TableRow>

          {
            !!location ? (
              <TableRow>
                <TableCell>{t("mission:cards:tasks:salvageTask:missionLocation")}</TableCell>
                <TableCell>{FormatLocation(location)}</TableCell>
              </TableRow>
            ) : null
          }

          {
            !!transport && location.id !== transport.id ? (
              <TableRow>
                <TableCell>{t("common:tables:transportTo")}</TableCell>
                <TableCell>{FormatLocation(transport)}</TableCell>
              </TableRow>
            ) : null
          }

          {
            !!task?.description ? (
              <TableRow>
                <TableCell>{t("mission:cards:tasks:salvageTask:description")}</TableCell>
                <TableCell>{task?.descriptiopn || "-"}</TableCell>
              </TableRow>
            ) : null
          }

          {
            !!task?.comment ? (
              <TableRow>
                <TableCell>{t("mission:cards:tasks:salvageTask:comment")}</TableCell>
                <TableCell>{task?.comment || "-"}</TableCell>
              </TableRow>
            ) : null
          }

          {
            !!task?.keyPlacement ? (
              <TableRow>
                <TableCell>{t("mission:cards:tasks:salvageTask:keyPlacement")}</TableCell>
                <TableCell>{task?.keyPlacement || "-"}</TableCell>
              </TableRow>
            ) : null
          }

          {
            !!task?.locationState ? (
              <TableRow>
                <TableCell>{t("mission:cards:tasks:salvageTask:customerWaiting")}</TableCell>
                <TableCell>{t(`mission:enums:locationState:${MissionEnumsLists.LocationState[task.locationState]}`)}</TableCell>
              </TableRow>
            ) : null
          }


          {
            images.length > 0 ? (
              <>
                <TableRow>
                  <TableCell colSpan={2}><Divider className={classes.divider} /></TableCell>
                </TableRow>

                <TableRow>
                  <TableCell colSpan={2}>
                    <Grid container rowSpacing={0} columnSpacing={0.5}>
                      {images.map((f, key) => (
                        <Grid item key={key}>
                          <img src={f.preview} className={classes.image}></img>
                        </Grid>
                      ))}
                    </Grid>
                  </TableCell>
                </TableRow>

              </>
            ) : null
          }

        </TableBody>
      </Table>

      {
        !slim ? (
          <>
            <DateAndTimePicker
              label={t("mission:cards:tasks:salvageTask:eta")}
              value={estimatedArrival.value}
              onChange={estimatedArrival.onChange}
              includeTime
              fromNow
            />

            <DriverDetails
              path={path}
              taskId={task.taskId}
            />

            <StateCard path={path} />
          </>
        ) : null
      }

    </Stack >
  );
}


